import axios from "axios";
import { observer } from "mobx-react-lite";
import React, {
  useState,
  ChangeEvent,
  MouseEvent,
  CSSProperties,
} from "react";
import { CSVLink } from "react-csv";
import { Icon } from "semantic-ui-react";
import { formatDate } from "./OperatorPerformanceNFAIReport";

type Filter = {
  searchName: string;
  searchValue: string;
};

type NFAIStatus = "QC" | "Approved" | "";

interface QualityCheckTypes {
  date: string;
  projectName: string;
  shift: string;
  team: string;

  operatorId: string;
  operatorName: string;

  qEmpId: string | null;
  qEmpName: string | null;

  process: string;
  resolution: string | null;
  reel: string | null;
  shot: string | null;

  startFrame: string;
  endFrame: string;
  totalFrame: string;

  appFrames: number;
  rejFrames: number;

  status: NFAIStatus;

  actualHr: string;
  extendHr: string;

  reportTime: string;
  allocationId: number;

  prodType: string;
  unit: string;
}

const NFAI_HEADERS: string[] = [
  "Date",
  "Project Name",
  "Shift",
  "Team",
  "Operator ID",
  "Operator Name",
  "QC Emp ID",
  "QC Emp Name",
  "Process",
  "Resolution",
  "Reel",
  "Shot",
  "Start Frame",
  "End Frame",
  "Total Frame",
  "Approved Frames",
  "Rejected Frames",
  "Status",
  "Actual Hr",
  "Extend Hr",
  "Report Time",
  "Allocation ID",
  "Prod Type",
  "Unit",
];

export const NFAI_ENTRY_INITIAL_VALUE: QualityCheckTypes = {
  date: "",
  projectName: "",
  shift: "",
  team: "",

  operatorId: "",
  operatorName: "",

  qEmpId: null,
  qEmpName: null,

  process: "",
  resolution: null,
  reel: null,
  shot: null,

  startFrame: "",
  endFrame: "",
  totalFrame: "",

  appFrames: 0,
  rejFrames: 0,

  status: "",

  actualHr: "00:00",
  extendHr: "00:00",

  reportTime: "",
  allocationId: 0,

  prodType: "",
  unit: "",
};


const QualityCheckPerformanceReport = () => {
  const today = new Date().toISOString().split("T")[0];

  const [reportData, SetReportData] = useState<QualityCheckTypes[]>(
    [NFAI_ENTRY_INITIAL_VALUE]
  );
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  // ✅ set default to Chennai so select matches option
  const [location, setLocation] = useState<string>("Chennai");

  const [loading, setLoading] = useState<boolean>(false);
  const [showData, setShowData] = useState<boolean>(false);

  const [filters, setFilters] = useState<Filter[]>([
    { searchName: "", searchValue: "" },
  ]);

    const [error, setError] = useState<string | null>(null);
  
    const handleSearch = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);

    try {
      const startDateFormatted = formatDate(startDate);
      const endDateFormatted = formatDate(endDate);

      const searchQuery = filters
        .filter(f => f.searchName && f.searchValue)
        .map(f => `${f.searchName}:${f.searchValue}`)
        .join(',');

      const params = new URLSearchParams();
      params.append('startDate', startDateFormatted);
      params.append('endDate', endDateFormatted);
      if (searchQuery) params.append('search', searchQuery);
      params.append('unit', location);

      const response = await axios.get(`/NFAIHour/NFAIOperatorPerformanceByDate?${params.toString()}`);
      SetReportData(Array.isArray(response.data) ? response.data : []);
      setShowData(true);
    } catch (err) {
      setError(axios.isAxiosError(err) ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters([{ searchName: "", searchValue: "" }]);
    setShowData(false);
  };

  const addFilter = () => {
    setFilters((prev) => [...prev, { searchName: "", searchValue: "" }]);
  };

  const removeFilter = (index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFilter = (index: number, field: keyof Filter, value: string) => {
    setFilters((prev) => {
      const newFilters = [...prev];
      newFilters[index] = {
        ...newFilters[index],
        [field]: value,
      };
      return newFilters;
    });
  };

  const styles: {
    container: CSSProperties;
    filterBox: CSSProperties;
    input: CSSProperties;
    select: CSSProperties;
    button: CSSProperties;
  } = {
    container: {
      margin: "auto",
      padding: "10px",
      background: "#ffffff",
      borderRadius: "20px",
      boxShadow: "0 20px 60px rgba(102, 126, 234, 0.15)",
      maxHeight: '800px',
      minHeight: '500px'
    },
    filterBox: {
      marginBottom: "25px",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "20px",
      flexWrap: "wrap",
      border: "2px solid #e5e7eb",
      padding: "10px",
      borderRadius: "16px",
      background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
      boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)",
    },
    input: {
      padding: "12px 16px",
      border: "2px solid #667eea",
      borderRadius: "10px",
      fontSize: "14px",
      backgroundColor: "#fff",
      color: "#333",
      width: "220px",
      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.1)",
      transition: "all 0.3s ease",
      outline: "none",
    },
    select: {
      width: "180px",
      padding: "12px 16px",
      border: "2px solid #667eea",
      borderRadius: "10px",
      fontSize: "14px",
      backgroundColor: "#fff",
      color: "#333",
      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.1)",
      transition: "all 0.3s ease",
      outline: "none",
      cursor: "pointer",
    },
    button: {
      padding: "12px 24px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      fontWeight: 600,
      cursor: "pointer",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
    },
  };

  const handleButtonMouseEnter = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = "translateY(-2px)";
  };

  const handleButtonMouseLeave = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = "translateY(0)";
  };

  return (
    <div style={styles.container}>
      <h2
        style={{
          textAlign: "center",
          marginBottom: "30px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          fontSize: "23px",
          fontWeight: 700,
          letterSpacing: "0.5px",
        }}
      >
        Quality Check Performance Report
      </h2>

      {/* Date Filters */}
      <div style={styles.filterBox}>
        <div>
          <label style={{ fontWeight: 600, marginRight: "8px", color: "#4a5568" }}>
            Start Date:
          </label>
          <input
            type="date"
            value={startDate}
            max={today}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setStartDate(e.target.value)
            }
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = "#764ba2")}
            onBlur={(e) => (e.target.style.borderColor = "#667eea")}
          />
        </div>

        <div>
          <label style={{ fontWeight: 600, marginRight: "8px", color: "#4a5568" }}>
            End Date:
          </label>
          <input
            type="date"
            value={endDate}
            max={today}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEndDate(e.target.value)
            }
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = "#764ba2")}
            onBlur={(e) => (e.target.style.borderColor = "#667eea")}
          />
        </div>

        <div>
          <label style={{ fontWeight: 600, marginRight: "8px", color: "#4a5568" }}>
            Location:
          </label>
          <select
            style={styles.select}
            value={location}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setLocation(e.target.value)
            }
            onFocus={(e) => (e.target.style.borderColor = "#764ba2")}
            onBlur={(e) => (e.target.style.borderColor = "#667eea")}
          >
            <option value="Chennai">Chennai</option>
            <option value="Hyderabad">Hyderabad</option>
          </select>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleSearch}
            style={styles.button}
            onMouseEnter={(e) => {
              handleButtonMouseEnter(e);
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(102, 126, 234, 0.6)";
            }}
            onMouseLeave={(e) => {
              handleButtonMouseLeave(e);
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(102, 126, 234, 0.4)";
            }}
          >
            Search
          </button>

          <button
            onClick={handleClear}
            style={{
              ...styles.button,
              background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
            }}
            onMouseEnter={(e) => {
              handleButtonMouseEnter(e);
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(107, 114, 128, 0.6)";
            }}
            onMouseLeave={(e) => {
              handleButtonMouseLeave(e);
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(102, 126, 234, 0.4)";
            }}
          >
            Clear Search
          </button>
        </div>
      </div>

      {/* Search Filters */}
      {showData && (
        <div style={{ ...styles.filterBox, marginBottom: "15px" }}>
          {filters.map((filter, index) => (
            <div key={index} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <select
                value={filter.searchName}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  updateFilter(index, "searchName", e.target.value)
                }
                style={styles.select}
              >
                <option value="">Select Field</option>
                {NFAI_HEADERS.map((field) => (
                  <option key={field} value={field}>
                    {field.toString().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={filter.searchValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateFilter(index, "searchValue", e.target.value)
                }
                placeholder="Search value"
                style={styles.input}
              />

              {index > 0 && (
                <button
                  onClick={() => removeFilter(index)}
                  style={{
                    ...styles.button,
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    padding: "10px",
                    boxShadow: "0 4px 15px rgba(239, 68, 68, 0.4)",
                  }}
                  onMouseEnter={(e) => {
                    handleButtonMouseEnter(e);
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(239, 68, 68, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    handleButtonMouseLeave(e);
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(239, 68, 68, 0.4)";
                  }}
                >
                  <Icon name="close" />
                </button>
              )}
            </div>
          ))}

          <button
            onClick={addFilter}
            style={{
              ...styles.button,
              background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
              boxShadow: "0 4px 15px rgba(139, 92, 246, 0.4)",
            }}
            onMouseEnter={(e) => {
              handleButtonMouseEnter(e);
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(139, 92, 246, 0.6)";
            }}
            onMouseLeave={(e) => {
              handleButtonMouseLeave(e);
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(139, 92, 246, 0.4)";
            }}
          >
            <Icon name="plus" />
            Add Filter
          </button>
        </div>
      )}

      {/* CSV Export */}
        {showData && (
          <div style={{ textAlign: "right", paddingBottom: "20px" }}>
           
              <button
                style={{
                  ...styles.button,
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.4)",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  handleButtonMouseEnter(e);
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(16, 185, 129, 0.6)";
                }}
                onMouseLeave={(e) => {
                  handleButtonMouseLeave(e);
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px rgba(16, 185, 129, 0.4)";
                }}
              >
                 <CSVLink
              data={reportData}
              filename={`StartDate_${startDate}_EndDate_${endDate}_Quality_Check_Performance.csv`}
              style={{ textDecoration: 'none', width: "100px", color: "white" }}
            >
                <Icon name="download" />
                Download
            </CSVLink>
              </button>
          </div>
        )}

      {/* Loading */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px 0" }}>
          <div className="loader"></div>
        </div>
      )}

      {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

      {/* Table */}
      {!loading && showData && reportData.length > 0 && (
        <div
          style={{
            background: "linear-gradient(135deg, #fef3f9 0%, #fff5f7 100%)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(118, 75, 162, 0.15)",
            border: "2px solid #fde2f3",
            transform: "scale(0.66)",
            transformOrigin: "top left",
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: "300px",
            maxHeight: "800px",
            width: "100%",
            minWidth: "fit-content"
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                <th
                  style={{
                    padding: "16px",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "14px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  S.No
                </th>

                {Object.keys(reportData[0]).map((col, i) => (
                  <th
                    key={i}
                    style={{
                      padding: "16px",
                      border: "none",
                      whiteSpace: "nowrap",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "14px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {reportData.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  style={{
                    backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#fef8fc",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff0f9";
                    e.currentTarget.style.transform = "scale(1.01)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      rowIdx % 2 === 0 ? "#ffffff" : "#fef8fc";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <td
                    style={{
                      padding: "14px",
                      border: "1px solid #fde2f3",
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#6b7280",
                    }}
                  >
                    {rowIdx + 1}
                  </td>

                  {Object.values(row).map((value, colIdx) => (
                    <td
                      key={colIdx}
                      style={{
                        padding: "14px",
                        border: "1px solid #fde2f3",
                        color: "#374151",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {String(value ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !showData && (
        <div
          style={{
            textAlign: "center",
            marginTop: "40px",
            padding: "40px",
            background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            border: "2px solid #bae6fd",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(56, 189, 248, 0.15)",
          }}
        >
          <p style={{ color: "#0369a1", fontSize: "16px", margin: 0, fontWeight: 500 }}>
            Select date range and location, then click Search to view data.
          </p>
        </div>
      )}

      <style>{`
        .loader {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          color: #667eea;
          position: relative;
          animation: spinLoader 1s infinite steps(8);
          box-shadow: 
            26px 0 0 0,
            18.385px 18.385px 0 2px,
            0 26px 0 4px,
            -18.385px 18.385px 0 6px,
            -26px 0 0 8px,
            -18.385px -18.385px 0 10px,
            0 -26px 0 12px;
        }
        
        @keyframes spinLoader {
          100% { transform: rotate(360deg); }
        }
        
        input:focus, select:focus {
          border-color: #764ba2 !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default observer(QualityCheckPerformanceReport);
